import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useUser } from '@/contexts/UserContext'
import { useShow } from '@/contexts/ShowContext'
import { useWebSocket } from '@/hooks/useWebSocket'
import { apiFetch } from '@/lib/api'

const TasksContext = createContext(null)

const POLL_INTERVAL_MS = 20000
const MANAGER_ROLE = 30

export function TasksProvider({ children }) {
  const { user } = useUser()
  const { show } = useShow()
  const [activeCount, setActiveCount] = useState(0)

  const showId = show?._id
  const isManager = (user?.role ?? 0) >= MANAGER_ROLE

  // Keep latest values for the interval/listener callbacks without re-subscribing.
  const showIdRef = useRef(showId)
  showIdRef.current = showId
  const isManagerRef = useRef(isManager)
  isManagerRef.current = isManager

  const refresh = useCallback(async () => {
    const currentShowId = showIdRef.current
    if (!currentShowId) {
      setActiveCount(0)
      return
    }
    try {
      const params = new URLSearchParams()
      params.set('show', currentShowId)
      params.set('size', '1')
      if (isManagerRef.current) params.set('scope', 'all')

      const response = await apiFetch(`/api/tasks?${params.toString()}`)
      if (!response.ok) throw new Error('Failed to fetch task count')
      const data = await response.json()
      setActiveCount(data.active || 0)
    } catch (error) {
      console.error('Error fetching active task count:', error)
    }
  }, [])

  // Editors receive their own task events live; managers rely on the poll below
  // for other users' tasks (events are only published to the owner's feed).
  useWebSocket(['task_updated', 'task_completed', 'task_failed', 'task_cancelled'], {
    onMessage: () => refresh(),
  })

  useEffect(() => {
    if (!user || !showId) {
      setActiveCount(0)
      return
    }
    refresh()
    const interval = setInterval(refresh, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [user, showId, refresh])

  return (
    <TasksContext.Provider value={{ activeCount, refresh }}>
      {children}
    </TasksContext.Provider>
  )
}

export function useTasks() {
  const context = useContext(TasksContext)
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider')
  }
  return context
}
